import type { ReportField } from '../../types/reporting.js';
import { expandMultiFilterModel, isSetFilterModel, planSetFilter } from '../../utils/gridFilterModels.ts';
import { postgrestValue } from '../../utils/postgrest.js';

/*
 * Report grid filter model -> PostgREST filters. Shared by the grid rows and
 * exact count, the column stats, the dashboard KPIs and exports, so every one
 * of them sees the same filter. No Supabase client here: callers pass the
 * query builder (tests use a bare PostgREST builder).
 */

function isPresent(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

/**
 * REG-671: upper bound of an inclusive date range, as an exclusive timestamp.
 *
 * A date range picked as 09/01 to 09/02 means "through the end of the 2nd", but
 * comparing a date-only value against a timestamp column makes `lte '2026-09-02'`
 * mean `<= 2026-09-02 00:00:00`, so every row later that same day is dropped.
 * On Funded that showed 3 of 6 deals while the summary cards, which do not go
 * through this path, correctly showed 6.
 *
 * Returns the following day so the caller can use a strict `lt`. Values that
 * already carry a time component are left alone — they are explicit.
 */
function exclusiveEndOfDay(value: unknown): string | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const next = new Date(`${value}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

/**
 * A date column filter as PostgREST comparisons on whole days.
 *
 * AG Grid's date filter sends `dateFrom`/`dateTo` ("2026-10-27 00:00:00"),
 * not `filter`/`filterTo`; reading only the latter dropped every date column
 * filter on the server grids, so rows, counts and KPIs came back unfiltered.
 * A picked date means the whole day on timestamp columns: "equals 10/27" is
 * `>= 10/27 and < 10/28`, "after 10/27" starts on 10/28, and a range includes
 * both end days (as REG-671's presets do). A value with a real time of day is
 * compared as that exact instant.
 */
interface DateLeaf { op: 'eq' | 'neq' | 'lt' | 'lte' | 'gt' | 'gte'; value: string }
interface DateClauses { join: 'and' | 'or'; leaves: DateLeaf[] }

const DAY_RE = /^(\d{4}-\d{2}-\d{2})(?:[ T]00:00(?::00(?:\.0+)?)?)?$/;

// AG Grid date filter shape; toolbar presets keep using filter/filterTo below
function isDateFilterModel(filter: any): boolean {
  return !isPresent(filter?.filter) && !isPresent(filter?.filterTo)
    && (isPresent(filter?.dateFrom) || isPresent(filter?.dateTo));
}

function dateFilterClauses(filter: any): DateClauses | null {
  const { type, dateFrom, dateTo } = filter;
  const leaf = (op: DateLeaf['op'], value: string): DateLeaf => ({ op, value });
  // [start, end) of the picked day, or the exact instant when a time is given
  const bounds = (value: unknown): { start: string; end: string | null } | null => {
    if (!isPresent(value)) return null;
    const raw = String(value).trim();
    const day = DAY_RE.exec(raw)?.[1];
    return day ? { start: day, end: exclusiveEndOfDay(day) } : { start: raw, end: null };
  };
  const from = bounds(dateFrom);

  if (type === 'inRange') {
    const to = bounds(dateTo);
    const leaves: DateLeaf[] = [];
    if (from) leaves.push(leaf('gte', from.start));
    if (to) leaves.push(to.end ? leaf('lt', to.end) : leaf('lte', to.start));
    return leaves.length > 0 ? { join: 'and', leaves } : null;
  }
  if (!from) return null;
  const { start, end } = from;
  switch (type) {
    case 'equals':
      return end ? { join: 'and', leaves: [leaf('gte', start), leaf('lt', end)] } : { join: 'and', leaves: [leaf('eq', start)] };
    case 'notEqual':
      return end ? { join: 'or', leaves: [leaf('lt', start), leaf('gte', end)] } : { join: 'and', leaves: [leaf('neq', start)] };
    case 'lessThan': return { join: 'and', leaves: [leaf('lt', start)] };
    case 'lessThanOrEqual': return { join: 'and', leaves: [end ? leaf('lt', end) : leaf('lte', start)] };
    case 'greaterThan': return { join: 'and', leaves: [end ? leaf('gte', end) : leaf('gt', start)] };
    case 'greaterThanOrEqual': return { join: 'and', leaves: [leaf('gte', start)] };
    default: return null;
  }
}

function dateLeafClause(col: string, { op, value }: DateLeaf): string {
  return `${col}.${op}.${postgrestValue(value)}`;
}

function dateClauseExpression(col: string, { join, leaves }: DateClauses): string {
  const parts = leaves.map((leaf) => dateLeafClause(col, leaf));
  return parts.length === 1 ? parts[0] : `${join}(${parts.join(',')})`;
}

function orClauseForFilter(col: string, filter: any): string | null {
  const { type, filter: val, filterTo } = filter;

  if (type === 'blank') return `${col}.is.null`;
  if (type === 'notBlank') return `${col}.not.is.null`;

  if (isDateFilterModel(filter)) {
    const clauses = dateFilterClauses(filter);
    return clauses ? dateClauseExpression(col, clauses) : null;
  }

  if (type === 'inRange') {
    const clauses: string[] = [];
    if (isPresent(val)) clauses.push(`${col}.gte.${postgrestValue(val)}`);
    if (isPresent(filterTo)) {
      const exclusive = exclusiveEndOfDay(filterTo);
      clauses.push(exclusive
        ? `${col}.lt.${postgrestValue(exclusive)}`
        : `${col}.lte.${postgrestValue(filterTo)}`);
    }
    if (clauses.length === 0) return null;
    return clauses.length === 1 ? clauses[0] : `and(${clauses.join(',')})`;
  }

  if (!isPresent(val)) return null;

  switch (type) {
    case 'equals': return `${col}.eq.${postgrestValue(val)}`;
    case 'notEqual': return `${col}.neq.${postgrestValue(val)}`;
    case 'contains': return `${col}.ilike.${postgrestValue(`*${val}*`)}`;
    case 'notContains': return `${col}.not.ilike.${postgrestValue(`*${val}*`)}`;
    case 'startsWith': return `${col}.ilike.${postgrestValue(`${val}*`)}`;
    case 'endsWith': return `${col}.ilike.${postgrestValue(`*${val}`)}`;
    case 'greaterThan': return `${col}.gt.${postgrestValue(val)}`;
    case 'greaterThanOrEqual': return `${col}.gte.${postgrestValue(val)}`;
    case 'lessThan': return `${col}.lt.${postgrestValue(val)}`;
    case 'lessThanOrEqual': return `${col}.lte.${postgrestValue(val)}`;
    default: return `${col}.ilike.${postgrestValue(`*${val}*`)}`;
  }
}

function applySingleFilter(query: any, col: string, filter: any): any {
  const { type, filter: val, filterTo } = filter;

  if (type === 'blank') return query.is(col, null);
  if (type === 'notBlank') return query.not(col, 'is', null);

  if (isDateFilterModel(filter)) {
    const clauses = dateFilterClauses(filter);
    if (!clauses) return query;
    // and-leaves are plain column filters; an or-pair is one `or` param
    if (clauses.join === 'or' && clauses.leaves.length > 1) {
      return query.or(clauses.leaves.map((leaf) => dateLeafClause(col, leaf)).join(','));
    }
    return clauses.leaves.reduce((q: any, { op, value }) => q.filter(col, op, value), query);
  }

  if (type === 'inRange') {
    if (isPresent(val)) query = query.gte(col, val);
    if (isPresent(filterTo)) {
      const exclusive = exclusiveEndOfDay(filterTo);
      query = exclusive ? query.lt(col, exclusive) : query.lte(col, filterTo);
    }
    return query;
  }

  if (!isPresent(val)) return query;

  switch (type) {
    case 'plain_fts': return query.textSearch(col, String(val), { type: 'plain', config: 'english' });
    case 'overlaps': return query.overlaps(col, val);
    case 'equals': return query.eq(col, val);
    case 'notEqual': return query.neq(col, val);
    case 'contains': return query.ilike(col, `%${val}%`);
    case 'notContains': return query.not(col, 'ilike', `%${val}%`);
    case 'startsWith': return query.ilike(col, `${val}%`);
    case 'endsWith': return query.ilike(col, `%${val}`);
    case 'greaterThan': return query.gt(col, val);
    case 'greaterThanOrEqual': return query.gte(col, val);
    case 'lessThan': return query.lt(col, val);
    case 'lessThanOrEqual': return query.lte(col, val);
    default: return query.ilike(col, `%${val}%`);
  }
}

export function applyReportFilters(
  query: any,
  filterModel: Record<string, any>,
  fields: ReportField[],
): any {
  Object.entries(expandMultiFilterModel(filterModel)).forEach(([modelKey, filter]) => {
    if (!filter) return;
    const { conditions, operator } = filter;
    const fieldName = typeof filter.colId === 'string' ? filter.colId : modelKey;
    // Set filters carry stable relation IDs. Text filters target the displayed label.
    const isSetFilter = isSetFilterModel(filter);
    const col = !isSetFilter && fields.some((f) => f.name === `${fieldName}_label`)
      ? `${fieldName}_label`
      : fieldName;

    if (isSetFilter) {
      const plan = planSetFilter(filter);
      const list = (items: unknown[]) => `(${items.map(postgrestValue).join(',')})`;
      if (plan.kind === 'none') {
        // Deselect all: an empty IN list matches no row.
        query = query.in(col, []);
      } else if (plan.kind === 'include') {
        if (plan.values.length === 0) query = query.is(col, null);
        else if (plan.includesNull) query = query.or(`${col}.in.${list(plan.values)},${col}.is.null`);
        else query = query.in(col, plan.values);
      } else if (plan.kind === 'exclude') {
        // NOT IN drops NULL rows on its own, so keep blanks unless they were unticked.
        if (plan.values.length === 0) query = query.not(col, 'is', null);
        else if (plan.excludesNull) query = query.not(col, 'in', list(plan.values)).not(col, 'is', null);
        else query = query.or(`${col}.not.in.${list(plan.values)},${col}.is.null`);
      }
      return;
    }

    const activeConditions = Array.isArray(conditions)
      ? conditions.filter(Boolean)
      : [filter.condition1, filter.condition2].filter(Boolean);
    const conditionsToApply = activeConditions.length > 0 ? activeConditions : [filter];

    if (conditionsToApply.length > 1 && operator === 'OR') {
      const clauses = conditionsToApply
        .map((condition) => orClauseForFilter(col, condition))
        .filter((clause): clause is string => Boolean(clause));
      if (clauses.length > 0) query = query.or(clauses.join(','));
      return;
    }

    conditionsToApply.forEach((condition) => {
      query = applySingleFilter(query, col, condition);
    });
  });
  return query;
}
